<template>
  <v-data-table
    class="dense-selection-controls"
    :headers="getHeaders"
    :items="items"
    :dense="dense"
    :mobile-breakpoint="0"
    :items-per-page="
      infinite || $vuetify.breakpoint.xsOnly ? items.length + 1 : 10
    "
    :hide-default-footer="infinite || $vuetify.breakpoint.xsOnly"
    item-key="id"
    multi-sort
    :sort-by="['ratio', 'quantity']"
    :sort-desc="[false, true]"
    hide-default-header
  >
    <template
      v-slot:header="{ props: { headers } }"
      v-if="$vuetify.breakpoint.smAndUp"
    >
      <thead>
        <tr>
          <th
            v-for="(header, index) in headers"
            :key="`${header.text}${index}`"
            class="column sortable"
            :style="{
              width: index === 0 ? '50px' : index === 1 ? '300px' : ''
            }"
          >
            {{ header.text }}
          </th>
        </tr>
      </thead>
    </template>
    <template v-slot:item.is_done="{ item }">
      <v-checkbox
        hide-details
        v-model="item.is_done"
        @change="toggleIsDone(item)"
      ></v-checkbox>
    </template>
    <template v-slot:item.nom="{ item }">
      <RecipeDialogViewer
        v-if="getRecipeId(item)"
        :id="getRecipeId(item)"
        :quantity="getRecipeQty(item)"
      ></RecipeDialogViewer>
      <span v-else :class="{ deleted: item.is_deleted }">{{ item.nom }}</span>
    </template>
    <template v-slot:item.quantity_inventory="{ item }">
      <span :class="{ deleted: item.is_deleted }">{{
        item.quantity_inventory | round2
      }}</span>
      <span class="overline ml-1" :class="{ deleted: item.is_deleted }">{{
        getProductUnit(item)
      }}</span>
    </template>
    <template v-slot:item.quantity_asked="{ item }">
      <span :class="{ deleted: item.is_deleted }">{{
        item.quantity_asked
      }}</span>
      <span class="overline ml-1" :class="{ deleted: item.is_deleted }">{{
        getProductUnit(item)
      }}</span>
    </template>
    <template v-slot:item.quantity="{ item }">
      <span
        class="font-weight-black"
        :class="{ deleted: item.is_deleted }"
        style="font-size: 1.3em"
        >{{ getInvQuantity(item) }}</span
      >
      <span
        class="overline font-weight-bold ml-1"
        :class="{ deleted: item.is_deleted }"
        >{{ getProductionProductUnit(item) }}</span
      >
    </template>
    <template v-slot:item.quantity_convert="{ item }">
      <span
        v-if="item.data.use_conversion_formula"
        :class="{ deleted: item.is_deleted }"
        >{{ getProdQty(item) }}</span
      >
      <span
        v-if="item.data.use_conversion_formula"
        :class="{ deleted: item.is_deleted }"
        class="overline ml-1"
        >{{ item.data.unite_mesure }}</span
      >
    </template>
    <template v-slot:item.quantity_recipe="{ item }">
      <span class="font-weight-bold" :class="{ deleted: item.is_deleted }">{{
        getRecipeQty(item)
      }}</span>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-row align="center" justify="end" no-gutters>
        <v-btn
          class="no-print"
          v-if="item.is_deleted === false"
          icon
          @click="deleteProductionProduct(item)"
        >
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-row>
    </template>
    <template v-slot:item.mobile="{ item }">
      <div class="my-1">
        <v-row align="center" justify="start" no-gutters>
          <RecipeDialogViewer
            v-if="getRecipeId(item)"
            :id="getRecipeId(item)"
            :quantity="getRecipeQty(item)"
          ></RecipeDialogViewer>
        </v-row>
        <v-row align="center" justify="start" no-gutters class="mt-1">
          <span
            class="font-weight-black"
            :class="{ deleted: item.is_deleted }"
            style="font-size: 1.3em"
            >{{ getInvQuantity(item) }}</span
          >
          <span
            class="overline font-weight-bold ml-1"
            :class="{ deleted: item.is_deleted }"
            >{{ getProductionProductUnit(item) }}</span
          >
        </v-row>
        <v-row align="center" justify="start" no-gutters class="mt-1">
          <span
            v-if="item.data.use_conversion_formula"
            :class="{ deleted: item.is_deleted }"
            >{{ getProdQty(item) }}</span
          >
          <span
            v-if="item.data.use_conversion_formula"
            :class="{ deleted: item.is_deleted }"
            class="overline ml-1"
            >{{ item.data.unite_mesure }}</span
          >
        </v-row>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import { api } from "@/api";
import { round10 } from "round10";
import { mappers } from "@/mappers";
import { mapGetters } from "vuex";
import { RecipeDialogViewer } from "@/modules/Recipes/components";
export default {
  name: "PreparationSlipViewerGrid",
  components: {
    RecipeDialogViewer
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    dense: {
      type: Boolean,
      default: false
    },
    infinite: {
      type: Boolean,
      default: false
    },
    hideHeaders: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(["getRecipes", "getPreparationSlips"]),
    getHeaders() {
      if (this.hideDetails && this.$vuetify.breakpoint.xsOnly) {
        return [{ value: "is_done", width: 50 }, { value: "mobile" }];
      } else if (
        this.hideDetails === false &&
        this.$vuetify.breakpoint.xsOnly
      ) {
        return [
          { value: "is_done", width: 50 },
          { value: "mobile" },
          { value: "actions", text: "", width: 50 }
        ];
      } else if (this.hideDetails) {
        return [
          { value: "is_done", text: "", width: 50 },
          { value: "nom", text: "Nom", width: 500 },
          { value: "quantity", text: "Production" },
          { value: "quantity_convert", text: "Production (conversion)" }
        ];
      } else {
        return [
          { value: "is_done", text: "", width: 50 },
          { value: "nom", text: "Nom", width: 300 },
          { value: "quantity_asked", text: "Minimum demandé" },
          { value: "quantity_inventory", text: "Inventaire" },
          { value: "quantity", text: "Production" },
          { value: "quantity_convert", text: "(conversion)" },
          { value: "actions", text: "", width: 50 }
        ];
      }
    }
  },
  methods: {
    getInvQuantity(item) {
      const recipe = this.getRecipes.find(
        x => x.fk_produit_intermediaire === item.fk_product
      );
      if (recipe.always_produce_quantity) {
        return this.getRecipeQty(item);
      } else {
        return round10(item.quantity, -2);
      }
    },
    getProductionProductUnit(item) {
      const recipe = this.getRecipes.find(
        x => x.fk_produit_intermediaire === item.fk_product
      );
      if (recipe) {
        if (recipe.always_produce_quantity) {
          return "recette";
        } else if (recipe.data && recipe.data.use_conversion_formula) {
          return recipe.data.unite_inventory;
        } else {
          return recipe.unite_produite;
        }
      } else {
        return item.data && item.data.unite_mesure_inventory
          ? item.data.unite_mesure_inventory
          : item.data && item.data.unite_mesure
          ? item.data.unite_mesure
          : "";
      }
    },
    getProductUnit(item) {
      const recipe = this.getRecipes.find(
        x => x.fk_produit_intermediaire === item.fk_product
      );
      if (recipe) {
        if (recipe.data && recipe.data.use_conversion_formula) {
          return recipe.data.unite_inventory;
        } else {
          return recipe.unite_produite;
        }
      } else {
        return item.data && item.data.unite_mesure_inventory
          ? item.data.unite_mesure_inventory
          : item.data && item.data.unite_mesure
          ? item.data.unite_mesure
          : "";
      }
    },
    getProdQty(item) {
      return round10(mappers.productInvToProd(item.quantity, item.data), -2);
    },
    getRecipeQty(item) {
      const recipe = this.getRecipes.find(
        x => x.fk_produit_intermediaire === item.fk_product
      );

      if (recipe.data && recipe.data.use_conversion_formula) {
        const recipeInProd = mappers.recipeInvToProd(
          item.quantity,
          recipe,
          recipe.data
        );
        return round10(recipeInProd / recipe.quantite_produite, -2);
      } else {
        return round10(item.quantity / recipe.quantite_produite, -2);
      }
    },
    getRecipeId(item) {
      const recipe = this.getRecipes.find(
        x => x.fk_produit_intermediaire === item.fk_product
      );
      return recipe ? recipe.id : undefined;
    },
    async deleteProductionProduct(item) {
      const allow = await this.$dialog.confirm({
        text: "Voulez-vous vraiment supprimer cette production?",
        title: ""
      });
      if (allow) {
        const prep_slip = this.getPreparationSlips.find(
          x => x.id === item.fk_preparation_slip
        );
        item.is_deleted = true;
        const cancelProductionData = {
          id: item.fk_product,
          quantity: -1 * Number(item.quantity),
          date: prep_slip.date
        };
        await api.postProduction(cancelProductionData);
        await api.patchPreparationSlipProduct(item.id, item);
      }
    },
    async toggleIsDone(item) {
      const d = {
        id: item.id,
        is_done: item.is_done
      };
      await api.patchPreparationSlipProduct(item.id, d);
    }
  }
};
</script>

<style lang="scss" scoped>
.deleted {
  text-decoration: line-through;
  opacity: 0.3;
  font-size: 1em !important;
}
</style>
